if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.closest('.ds-product-buy') || !('IntersectionObserver' in window)) return;
        this._visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            entry.target.dataset.galleryVisible = String(entry.isIntersecting && entry.intersectionRatio >= 0.65);
            if (!entry.isIntersecting || entry.intersectionRatio < 0.65) {
              if (video) video.pause();
              return;
            }
            // Horizontal mobile swipes do not always select a thumbnail. The
            // visible slide, rather than the desktop is-active class, owns play.
            if (entry.target.querySelector('.deferred-media')) {
              this.setActiveSlide(entry.target);
              this.playActiveMedia(entry.target);
            }
          });
        }, { threshold: [0, 0.65] });
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          const video = item.querySelector('template')?.content.querySelector('video[muted][autoplay]');
          if (video) this._visibilityObserver.observe(item);
        });
        this._onVisibilityChange = () => {
          this.querySelectorAll('video').forEach((video) => video.pause());
          if (!document.hidden) {
            const visible = this.elements.viewer.querySelector('[data-gallery-visible="true"]');
            if (visible) this.playActiveMedia(visible);
          }
        };
        document.addEventListener('visibilitychange', this._onVisibilityChange);
      }

      disconnectedCallback() {
        if (this._visibilityObserver) this._visibilityObserver.disconnect();
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this.querySelectorAll('video').forEach((video) => video.pause());
      }

      onSlideChanged(event) {
        this.setActiveSlide(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.setActiveSlide(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const alignMobileMedia = !this.mql.matches && this.hasAttribute('data-mobile-media-alignment');
            const firstMedia = activeMedia.parentElement.querySelector('[data-media-id]');
            activeMedia.parentElement.scrollTo({
              left: alignMobileMedia ? activeMedia.offsetLeft - firstMedia.offsetLeft : activeMedia.offsetLeft,
            });
          }
          // Craft scrolled the page back up to the active media here whenever
          // it sat above the viewport. Removed 16/09/2026: on the SpringRopes
          // page the colour swatches live below the gallery, so every colour
          // change yanked the visitor to the top of the page. The gallery
          // still switches to the variant's image; the page stays put.
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveSlide(activeItem) {
        // Mobile CSS only reveals inline video on the active slide. Native
        // swipe/arrow events must update this state just like thumbnail taps.
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.toggle('is-active', item === activeItem);
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          const restricted = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            (navigator.connection && navigator.connection.saveData) || document.hidden;
          const templateVideo = deferredMedia.querySelector('template')?.content.querySelector('video');
          // Do not fetch the video automatically when the shopper opts out.
          if (restricted && !deferredMedia.hasAttribute('loaded')) return;
          deferredMedia.loadContent(false, false);
          const video = deferredMedia.querySelector('video');
          if (!video) return;
          video.muted = video.hasAttribute('muted');
          video.playsInline = true;
          // Respect the gallery template: the muted looping demo has no controls.
          // Keep native controls available when automatic playback is restricted.
          video.controls = restricted || !templateVideo || templateVideo.hasAttribute('controls');
          if (restricted || !(templateVideo && templateVideo.hasAttribute('autoplay'))) return;
          video.play().catch(() => { video.controls = true; });
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
